import { useState } from 'react';
import { Map as GoogleMap, Marker, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { themeClasses, useTheme, getMapStyles } from '@member-portal/ui';

interface LocationMapProps {
  latitude: number;
  longitude: number;
  name?: string;
  className?: string;
}

function LocationMap({ latitude, longitude, name, className = '' }: LocationMapProps) {
  const apiIsLoaded = useApiIsLoaded();
  const { isLightTheme } = useTheme();
  const [mapActive, setMapActive] = useState(false);

  const handleMapClick = () => {
    if (!mapActive) {
      setMapActive(true);
    }
  };

  // Generate marker icon
  const getMarkerIcon = (): google.maps.Icon | undefined => {
    if (!apiIsLoaded || typeof window === 'undefined' || !window.google?.maps) return undefined;

    const size = 36;
    const fillColor = isLightTheme ? '#000000' : '#ffffff';
    const strokeColor = isLightTheme ? '#333333' : '#000000';
    const centerColor = isLightTheme ? '#ffffff' : '#000000';

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"
             xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13
                   c0-3.87-3.13-7-7-7z"
                fill="${fillColor}" stroke="${strokeColor}" stroke-width="1.5"/>
          <circle cx="12" cy="9" r="2.5" fill="${centerColor}" opacity="0.8"/>
        </svg>
      `)}`,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size)
    };
  };

  return (
    <div className={`relative w-full h-full min-h-[300px] ${className}`}>
      {/* Click-to-activate overlay */}
      {!mapActive && (
        <div
          className={`absolute inset-0 z-10 flex items-end justify-center pb-4 cursor-pointer ${isLightTheme ? 'bg-white/30' : 'bg-black/30'}`}
          onClick={handleMapClick}
        >
          <div className="bg-theme-bg text-theme-text px-4 py-2 border border-theme-border flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" clipRule="evenodd" />
            </svg>
            <span className={themeClasses.text.mutedSmall}>Click to interact</span>
          </div>
        </div>
      )}

      <GoogleMap
        defaultCenter={{ lat: latitude, lng: longitude }}
        defaultZoom={14}
        styles={getMapStyles(isLightTheme)}
        gestureHandling={mapActive ? 'greedy' : 'none'}
        disableDefaultUI={true}
        zoomControl={mapActive}
        streetViewControl={false}
        mapTypeControl={false}
        fullscreenControl={false}
        clickableIcons={false}
        onClick={handleMapClick}
      >
        {apiIsLoaded && (
          <Marker
            position={{ lat: latitude, lng: longitude }}
            title={name}
            icon={getMarkerIcon()}
          />
        )}
      </GoogleMap>
    </div>
  );
}

export default LocationMap;
